/**
 * Test Runner Service
 *
 * Per-endpoint test methods following cost_test_client.py patterns.
 * When running all tests, a single shared session is used for tests
 * that don't need their own session lifecycle, to simulate real usage.
 *
 * Console logging shows step-by-step progress for each test.
 */

import type { TestResult, ChatEvent } from '../types'
import {
  startChat,
  stopChat,
  sendMessage,
  updateMemory,
  listCloudSessions,
  loadCloudSession,
  getSessionCosts,
} from './api'

type TestProgressCallback = (name: string, status: 'running' | 'passed' | 'failed') => void

/**
 * Normalize message content to a plain string.
 * Backend messages may have content as a string or as an array of content blocks.
 */
function normalizeContent(content: unknown): string {
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content
      .map((block: Record<string, unknown>) => {
        if (typeof block === 'string') return block
        if (block.type === 'text' && typeof block.text === 'string') return block.text
        return ''
      })
      .filter(Boolean)
      .join('\n')
  }
  return String(content || '')
}

function normalizeMessages(messages: Array<Record<string, unknown>>): Array<Record<string, string>> {
  return messages
    .filter(m => m.role === 'user' || m.role === 'assistant')
    .map(m => ({ role: String(m.role), content: normalizeContent(m.content) }))
    .filter(m => m.content.length > 0)
}

// ── Test definitions ────────────────────────────────────────────
// Each test can either:
//  - manage its own session (needsOwnSession: true)
//  - accept a shared session voiceChatId

interface TestDef {
  name: string
  needsOwnSession: boolean
  fn: (sharedVoiceChatId?: string) => Promise<TestResult>
}

async function startStopRoundtrip(): Promise<TestResult> {
  const start = performance.now()
  const name = 'Start/Stop Roundtrip'
  try {
    console.log(`[${name}] Starting session...`)
    const startRes = await startChat({ personaId: 'pipar' })
    if (!startRes.voice_chat_id) {
      return { name, passed: false, details: 'No voice_chat_id returned', duration: performance.now() - start }
    }
    console.log(`[${name}] Session created: ${startRes.voice_chat_id.slice(0, 12)}...`)

    console.log(`[${name}] Stopping session...`)
    const stopRes = await stopChat(startRes.voice_chat_id, 'pipar')
    console.log(`[${name}] Session stopped. Messages: ${(stopRes.messages || []).length}`)

    console.log(`[${name}] Fetching session costs...`)
    const costInfo = await getSessionCosts(startRes.voice_chat_id)
    console.log(`[${name}] Costs: ${costInfo ? JSON.stringify(costInfo).slice(0, 100) : 'none'}`)

    return {
      name,
      passed: true,
      details: `Session ${startRes.voice_chat_id.slice(0, 8)}... created and stopped. Messages returned: ${(stopRes.messages || []).length}`,
      duration: performance.now() - start,
      costInfo: costInfo ?? undefined,
    }
  } catch (e) {
    console.error(`[${name}] Error:`, e)
    return { name, passed: false, details: String(e), duration: performance.now() - start }
  }
}

async function chatRoundtrip(sharedVoiceChatId?: string): Promise<TestResult> {
  const start = performance.now()
  const name = 'Chat Roundtrip'
  try {
    let voiceChatId: string
    let ownSession = false
    if (sharedVoiceChatId) {
      voiceChatId = sharedVoiceChatId
      console.log(`[${name}] Using shared session: ${voiceChatId.slice(0, 12)}...`)
    } else {
      console.log(`[${name}] Starting session...`)
      const startRes = await startChat({ personaId: 'pipar' })
      voiceChatId = startRes.voice_chat_id
      ownSession = true
      console.log(`[${name}] Session: ${voiceChatId.slice(0, 12)}...`)
    }

    console.log(`[${name}] Sending message: "Say hello in exactly one word."`)
    let responseText = ''
    let costInfo: Record<string, unknown> | undefined
    let eventCount = 0
    for await (const event of sendMessage(voiceChatId, 'Say hello in exactly one word.')) {
      eventCount++
      if (event.type === 'text' || event.type === 'content') {
        responseText += (typeof event.data === 'string' ? event.data : '') || ''
      }
      if (event.type === 'complete' && event.cost_info) {
        costInfo = event.cost_info
      }
      if (event.type === 'error') {
        console.error(`[${name}] Stream error:`, event.data)
        if (ownSession) await stopChat(voiceChatId, 'pipar')
        return { name, passed: false, details: `Stream error: ${event.data}`, duration: performance.now() - start }
      }
    }
    console.log(`[${name}] Response (${eventCount} events): "${responseText.slice(0, 100)}"`)

    if (ownSession) {
      console.log(`[${name}] Stopping session...`)
      await stopChat(voiceChatId, 'pipar')
    }

    return {
      name,
      passed: responseText.length > 0,
      details: `Response: "${responseText.slice(0, 100)}"${costInfo ? `, cost tracked` : ', no cost_info'}`,
      duration: performance.now() - start,
      costInfo,
    }
  } catch (e) {
    console.error(`[${name}] Error:`, e)
    return { name, passed: false, details: String(e), duration: performance.now() - start }
  }
}

async function cloudSessionPersistence(): Promise<TestResult> {
  const start = performance.now()
  const name = 'Cloud Session Persistence'
  try {
    console.log(`[${name}] Starting session...`)
    const startRes = await startChat({ personaId: 'pipar' })
    const voiceChatId = startRes.voice_chat_id
    console.log(`[${name}] Session: ${voiceChatId.slice(0, 12)}...`)

    console.log(`[${name}] Sending message...`)
    let responseText = ''
    for await (const event of sendMessage(voiceChatId, 'Remember: my test color is cerulean.')) {
      if (event.type === 'text' || event.type === 'content') {
        responseText += (typeof event.data === 'string' ? event.data : '') || ''
      }
    }
    console.log(`[${name}] Response: "${responseText.slice(0, 80)}"`)

    console.log(`[${name}] Stopping session...`)
    await stopChat(voiceChatId, 'pipar')

    console.log(`[${name}] Listing cloud sessions...`)
    const { sessions } = await listCloudSessions('pipar')
    const found = sessions.find((s) => s.id === voiceChatId)
    if (!found) {
      console.error(`[${name}] Session not found in list (${sessions.length} sessions)`)
      return { name, passed: false, details: `Session ${voiceChatId.slice(0, 8)}... not found in cloud_sessions list (${sessions.length} sessions returned)`, duration: performance.now() - start }
    }
    console.log(`[${name}] Session found in list`)

    console.log(`[${name}] Loading session messages...`)
    const loaded = await loadCloudSession(voiceChatId)
    const msgCount = loaded.messages?.length ?? 0
    console.log(`[${name}] Loaded ${msgCount} messages`)

    return {
      name,
      passed: msgCount > 0,
      details: `Session persisted: ${msgCount} messages stored. AI response: "${responseText.slice(0, 60)}"`,
      duration: performance.now() - start,
    }
  } catch (e) {
    console.error(`[${name}] Error:`, e)
    return { name, passed: false, details: String(e), duration: performance.now() - start }
  }
}

async function memoryUpdateRoundtrip(sharedVoiceChatId?: string): Promise<TestResult> {
  const start = performance.now()
  const name = 'Memory Update Roundtrip'
  try {
    let voiceChatId: string
    let ownSession = false
    if (sharedVoiceChatId) {
      voiceChatId = sharedVoiceChatId
      console.log(`[${name}] Using shared session: ${voiceChatId.slice(0, 12)}...`)
    } else {
      console.log(`[${name}] Starting session...`)
      const startRes = await startChat({ personaId: 'pipar' })
      voiceChatId = startRes.voice_chat_id
      ownSession = true
      console.log(`[${name}] Session: ${voiceChatId.slice(0, 12)}...`)
    }

    console.log(`[${name}] Sending message...`)
    for await (const event of sendMessage(voiceChatId, 'My favorite programming language is Rust and I work at a startup.')) {
      // consume
    }

    // For memory update, we need to stop a session to get history.
    // If shared, stop the shared session, update memory, then start a new one.
    let history: Array<Record<string, string>>
    let discussionId: string

    if (ownSession) {
      console.log(`[${name}] Stopping session...`)
      const stopRes = await stopChat(voiceChatId, 'pipar')
      const rawHistory = stopRes.messages || []
      history = normalizeMessages(rawHistory as Array<Record<string, unknown>>)
      discussionId = stopRes.discussion_id || `test_${Date.now()}`
    } else {
      // Use synthetic history since we can't stop the shared session
      history = [
        { role: 'user', content: 'My favorite programming language is Rust and I work at a startup.' },
        { role: 'assistant', content: 'That sounds great! Rust is an excellent choice for systems programming.' },
      ]
      discussionId = `test_${Date.now()}`
    }

    console.log(`[${name}] Triggering memory update with ${history.length} messages...`)
    const finalHistory = history.length >= 2
      ? history
      : [
          { role: 'user', content: 'My favorite programming language is Rust.' },
          { role: 'assistant', content: 'Rust is a great language!' },
        ]

    const memResult = await updateMemory({
      discussionId,
      messageHistory: finalHistory,
      personaName: 'pipar',
      personaId: 'pipar',
    })
    console.log(`[${name}] Memory updated. Success: ${memResult.success}, cost: $${memResult.cost || 0}`)

    return {
      name,
      passed: Boolean(memResult.success),
      details: `Memory updated. Cost: $${memResult.cost || 0}. user_details: "${String(memResult.user_details || '').slice(0, 80)}"`,
      duration: performance.now() - start,
    }
  } catch (e) {
    console.error(`[${name}] Error:`, e)
    return { name, passed: false, details: String(e), duration: performance.now() - start }
  }
}

async function memoryLoadOnStart(): Promise<TestResult> {
  const start = performance.now()
  const name = 'Memory Load on Start'
  try {
    console.log(`[${name}] Starting session (expecting cloud memory load)...`)
    const startRes = await startChat({ personaId: 'pipar' })
    const voiceChatId = startRes.voice_chat_id

    const hasMemory = startRes.cloud_memory && Object.keys(startRes.cloud_memory).length > 0
    console.log(`[${name}] Cloud memory loaded: ${hasMemory ? 'yes' : 'no (expected on first run)'}`)
    if (hasMemory) {
      console.log(`[${name}] Memory keys: ${Object.keys(startRes.cloud_memory!).join(', ')}`)
    }

    console.log(`[${name}] Stopping session...`)
    await stopChat(voiceChatId, 'pipar')

    return {
      name,
      passed: true,
      details: hasMemory
        ? `Cloud memory loaded: ${JSON.stringify(startRes.cloud_memory).slice(0, 100)}`
        : 'No cloud memory yet (expected on first run)',
      duration: performance.now() - start,
    }
  } catch (e) {
    console.error(`[${name}] Error:`, e)
    return { name, passed: false, details: String(e), duration: performance.now() - start }
  }
}

async function searchMemoryServerSide(sharedVoiceChatId?: string): Promise<TestResult> {
  const start = performance.now()
  const name = 'Search Memory (Server-Side)'
  try {
    let voiceChatId: string
    let ownSession = false
    if (sharedVoiceChatId) {
      voiceChatId = sharedVoiceChatId
      console.log(`[${name}] Using shared session: ${voiceChatId.slice(0, 12)}...`)
    } else {
      console.log(`[${name}] Starting session...`)
      const startRes = await startChat({ personaId: 'pipar' })
      voiceChatId = startRes.voice_chat_id
      ownSession = true
      console.log(`[${name}] Session: ${voiceChatId.slice(0, 12)}...`)
    }

    console.log(`[${name}] Sending memory search prompt...`)
    let responseText = ''
    let eventCount = 0
    for await (const event of sendMessage(voiceChatId, 'What do you remember about my programming preferences from previous conversations?')) {
      eventCount++
      if (event.type === 'text' || event.type === 'content') {
        responseText += (typeof event.data === 'string' ? event.data : '') || ''
      }
      if (event.type === 'error') {
        console.error(`[${name}] Stream error:`, event.data)
      }
      if (event.type === 'complete') {
        const d = event.data as Record<string, unknown> | undefined
        console.log(`[${name}] Complete event: cloud_tools_executed=${d?.cloud_tools_executed}, tool_calls=${d?.tool_calls}`)
      }
    }
    console.log(`[${name}] Got ${eventCount} events, response length: ${responseText.length}`)
    if (responseText.length > 0) {
      console.log(`[${name}] Response: "${responseText.slice(0, 150)}"`)
    }

    if (ownSession) {
      console.log(`[${name}] Stopping session...`)
      await stopChat(voiceChatId, 'pipar')
    }

    return {
      name,
      passed: responseText.length > 0,
      details: responseText.length > 0
        ? `Server-side tool auto-continued. Response: "${responseText.slice(0, 100)}"`
        : `No response text received (${eventCount} events) — tool auto-continuation may have failed`,
      duration: performance.now() - start,
    }
  } catch (e) {
    console.error(`[${name}] Error:`, e)
    return { name, passed: false, details: String(e), duration: performance.now() - start }
  }
}

async function costTracking(sharedVoiceChatId?: string): Promise<TestResult> {
  const start = performance.now()
  const name = 'Cost Tracking'
  try {
    let voiceChatId: string
    let ownSession = false
    if (sharedVoiceChatId) {
      voiceChatId = sharedVoiceChatId
      console.log(`[${name}] Using shared session: ${voiceChatId.slice(0, 12)}...`)
    } else {
      console.log(`[${name}] Starting session...`)
      const startRes = await startChat({ personaId: 'pipar' })
      voiceChatId = startRes.voice_chat_id
      ownSession = true
      console.log(`[${name}] Session: ${voiceChatId.slice(0, 12)}...`)
    }

    console.log(`[${name}] Sending message: "Say hi."`)
    let eventCount = 0
    let gotError = false
    for await (const event of sendMessage(voiceChatId, 'Say hi.')) {
      eventCount++
      if (event.type === 'error') {
        console.error(`[${name}] Stream error:`, event.data)
        gotError = true
        break
      }
      if (event.type === 'complete') {
        const d = event.data as Record<string, unknown> | undefined
        console.log(`[${name}] Complete event received. cost_info present: ${!!d}`)
      }
    }
    console.log(`[${name}] Stream done (${eventCount} events, error: ${gotError})`)

    console.log(`[${name}] Fetching session costs from /costs/session/${voiceChatId.slice(0, 12)}...`)
    const costs = await getSessionCosts(voiceChatId)
    console.log(`[${name}] Cost data: ${costs ? JSON.stringify(costs).slice(0, 200) : 'null (no data)'}`)

    if (ownSession) {
      console.log(`[${name}] Stopping session...`)
      await stopChat(voiceChatId, 'pipar')
      console.log(`[${name}] Session stopped.`)
    }

    return {
      name,
      passed: costs !== null,
      details: costs
        ? `Session costs: ${JSON.stringify(costs).slice(0, 200)}`
        : 'No cost data returned from /costs/session endpoint',
      duration: performance.now() - start,
      costInfo: costs ?? undefined,
    }
  } catch (e) {
    console.error(`[${name}] Error:`, e)
    return { name, passed: false, details: String(e), duration: performance.now() - start }
  }
}

// ── Test Runner ─────────────────────────────────────────────────

const ALL_TESTS: TestDef[] = [
  { name: 'Start/Stop Roundtrip', needsOwnSession: true, fn: startStopRoundtrip },
  { name: 'Chat Roundtrip', needsOwnSession: false, fn: chatRoundtrip },
  { name: 'Cloud Session Persistence', needsOwnSession: true, fn: cloudSessionPersistence },
  { name: 'Memory Update Roundtrip', needsOwnSession: false, fn: memoryUpdateRoundtrip },
  { name: 'Memory Load on Start', needsOwnSession: true, fn: memoryLoadOnStart },
  { name: 'Search Memory (Server-Side)', needsOwnSession: false, fn: searchMemoryServerSide },
  { name: 'Cost Tracking', needsOwnSession: false, fn: costTracking },
]

export function getTestList(): string[] {
  return ALL_TESTS.map((t) => t.name)
}

export async function runTest(testName: string): Promise<TestResult> {
  const test = ALL_TESTS.find((t) => t.name === testName)
  if (!test) {
    return { name: testName, passed: false, details: 'Test not found', duration: 0 }
  }
  return test.fn()
}

/**
 * Run all tests. Tests that can share a session use a single shared session.
 * Tests that need their own session lifecycle (start/stop, persistence, memory load)
 * still create and manage their own.
 */
export async function runAllTests(
  onProgress?: TestProgressCallback,
): Promise<TestResult[]> {
  const results: TestResult[] = []

  // Start a shared session for tests that don't need their own
  let sharedVoiceChatId: string | undefined
  try {
    console.log('[RunAll] Starting shared session...')
    const startRes = await startChat({ personaId: 'pipar' })
    sharedVoiceChatId = startRes.voice_chat_id
    console.log(`[RunAll] Shared session: ${sharedVoiceChatId.slice(0, 12)}...`)
  } catch (e) {
    console.error('[RunAll] Failed to start shared session:', e)
  }

  for (const test of ALL_TESTS) {
    onProgress?.(test.name, 'running')
    const result = test.needsOwnSession
      ? await test.fn()
      : await test.fn(sharedVoiceChatId)
    onProgress?.(test.name, result.passed ? 'passed' : 'failed')
    results.push(result)
  }

  // Stop the shared session
  if (sharedVoiceChatId) {
    try {
      console.log('[RunAll] Stopping shared session...')
      await stopChat(sharedVoiceChatId, 'pipar')
      console.log('[RunAll] Shared session stopped.')
    } catch (e) {
      console.warn('[RunAll] Failed to stop shared session:', e)
    }
  }

  return results
}
